import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { 
    LayoutDashboard, 
    Package, 
    FolderTree, 
    MessageSquareQuote, 
    TableProperties, 
    Settings,
    LogOut,
    Menu,
    ChevronRight,
    ShoppingBag,
    Wrench,
    FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { createAdminApiClient } from "../lib/api";

const AdminLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    // Check if admin is logged in
    useEffect(() => {
        const credentials = sessionStorage.getItem('adminCredentials');
        if (!credentials) {
            navigate('/admin/login');
            return;
        }
        
        // Verify credentials are still valid
        const { username, password } = JSON.parse(credentials);
        const adminApi = createAdminApiClient(username, password);
        adminApi.login().catch(() => {
            sessionStorage.removeItem('adminCredentials');
            navigate('/admin/login');
        });
    }, [navigate]);
    
    const handleLogout = () => {
        sessionStorage.removeItem('adminCredentials');
        navigate('/admin/login');
    };
    
    const navItems = [
        { href: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
        { href: "/admin/products", icon: Package, label: "Products" },
        { href: "/admin/categories", icon: FolderTree, label: "Categories" },
        { href: "/admin/quotes", icon: MessageSquareQuote, label: "Quote Requests" },
        { href: "/admin/comparisons", icon: TableProperties, label: "Comparison Tables" },
        { href: "/admin/orders", icon: ShoppingBag, label: "Orders" },
        { href: "/admin/rentals", icon: Wrench, label: "Rental Fleet" },
        { href: "/admin/blog", icon: FileText, label: "Blog Posts" },
        { href: "/admin/settings", icon: Settings, label: "Site Settings" },
    ];
    
    const isActive = (href, exact = false) => {
        if (exact) return location.pathname === href;
        return location.pathname.startsWith(href);
    };
    
    const NavLink = ({ item }) => (
        <Link
            to={item.href}
            className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                isActive(item.href, item.exact) 
                    ? "bg-primary/10 text-primary border-l-2 border-primary" 
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground border-l-2 border-transparent"
            }`}
            data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
        >
            <item.icon className="h-5 w-5" />
            <span className="font-mono text-sm uppercase tracking-wider">{item.label}</span>
        </Link>
    );
    
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location]);
    
    return (
        <div className="min-h-screen flex bg-background">
            {/* Desktop Sidebar */}
            <aside className="hidden lg:flex flex-col w-64 bg-card border-r border-border">
                {/* Logo */}
                <div className="p-6 border-b border-border">
                    <Link to="/admin" className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary flex items-center justify-center">
                            <span className="font-heading font-black text-primary-foreground text-xl">DI</span>
                        </div>
                        <div>
                            <span className="font-heading font-bold text-foreground">ADMIN</span>
                        </div>
                    </Link>
                </div>
                
                {/* Navigation */}
                <nav className="flex-1 py-4">
                    {navItems.map((item) => (
                        <NavLink key={item.href} item={item} />
                    ))}
                </nav>
                
                {/* Bottom Actions */}
                <div className="p-4 border-t border-border">
                    <Link to="/" className="block mb-2">
                        <Button variant="outline" className="w-full justify-start gap-2 rounded-none">
                            <ChevronRight className="h-4 w-4" />
                            <span className="font-mono text-xs uppercase tracking-wider">View Site</span>
                        </Button>
                    </Link>
                    <Button 
                        variant="ghost" 
                        className="w-full justify-start gap-2 text-muted-foreground hover:text-destructive rounded-none"
                        onClick={handleLogout}
                        data-testid="admin-logout-btn"
                    >
                        <LogOut className="h-4 w-4" />
                        <span className="font-mono text-xs uppercase tracking-wider">Logout</span>
                    </Button>
                </div>
            </aside>
            
            {/* Main Content */}
            <div className="flex-1 flex flex-col min-h-screen">
                {/* Mobile Header */}
                <header className="lg:hidden sticky top-0 z-50 bg-card border-b border-border">
                    <div className="flex items-center justify-between px-4 h-16">
                        <Link to="/admin" className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-primary flex items-center justify-center">
                                <span className="font-heading font-black text-primary-foreground">DI</span>
                            </div>
                            <span className="font-heading font-bold text-foreground">ADMIN</span>
                        </Link>
                        
                        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon">
                                    <Menu className="h-6 w-6" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="left" className="w-72 bg-card border-border p-0">
                                <div className="p-6 border-b border-border">
                                    <span className="font-heading font-bold text-foreground">ADMIN MENU</span>
                                </div>
                                <nav className="py-4">
                                    {navItems.map((item) => (
                                        <NavLink key={item.href} item={item} />
                                    ))}
                                </nav>
                                <div className="p-4 border-t border-border">
                                    <Button 
                                        variant="ghost" 
                                        className="w-full justify-start gap-2 text-muted-foreground hover:text-destructive rounded-none"
                                        onClick={handleLogout}
                                    >
                                        <LogOut className="h-4 w-4" />
                                        <span className="font-mono text-xs uppercase tracking-wider">Logout</span>
                                    </Button>
                                </div>
                            </SheetContent>
                        </Sheet>
                    </div>
                </header>
                
                {/* Page Content */}
                <main className="flex-1 p-6 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
